<template>
  <div class="s-layout" :style="colorObject">
    <div class="s-container">
      <div class="content">
        <div class="page">
          <div class="page__title">{{ displayTitle }}</div>

          <div class="game-container">
            <div class="status">{{ statusText }}</div>
            <canvas ref="gameCanvas"></canvas>
            <div v-if="gameWon" class="win-message">
              🎉 恭喜！您完成了迷宮！<br>
              <SharedButton class="s-btn s-btn-bg-primary s-btn-xs" @click="resetGame" style="margin-top: 15px;">
                再玩一次
              </SharedButton>
            </div>
            <div class="instructions">
              傾斜設備來控制彈珠移動<br>
              避開紅色障礙物<br>
              到達綠色終點
            </div>
          </div>

          <div class="controls">
            <SharedButton 
              class="s-btn s-btn-bg-primary s-btn-xs" 
              :disabled="gameRunning" 
              @click="startGame"
            >
              {{ startButtonText }}
            </SharedButton>
            <SharedButton 
              class="s-btn s-btn-bg-secondary s-btn-xs" 
              @click="testMovement" 
              style="margin-left: 10px;"
            >
              測試移動
            </SharedButton>
            <div class="info">請允許陀螺儀權限以開始遊戲，或使用鍵盤方向鍵</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import themeColor from "@/mixins/liff/themeColor";
import { mapGetters } from "vuex";
import SharedButton from "@/components/Page/Liff/Shared/Button";

export default {
  mixins: [themeColor],
  components: {
    SharedButton,
  },
  computed: {
    ...mapGetters("liffGeneral", ["themeConfig", "meta"]),
    displayTitle() {
      return this.event?.name ?? "陀螺儀彈珠迷宮";
    },
    startButtonText() {
      if (!this.gameRunning) return "開始遊戲";
      if (this.requestingPermission) return "請求權限中...";
      return "遊戲進行中...";
    },
    statusText() {
      if (this.gameWon) return "遊戲完成！";
      if (!this.gameRunning) return "準備開始";
      if (this.requestingPermission) return "正在請求陀螺儀權限...";
      if (this.gyroAvailable) return "陀螺儀權限已授予，請傾斜設備";
      if (this.gyroDenied) return "陀螺儀權限被拒絕，請手動允許";
      if (!this.gyroSupported) return "此設備不支援陀螺儀，請使用鍵盤方向鍵";
      return `陀螺儀: X=${this.gyroData.x.toFixed(1)}, Y=${this.gyroData.y.toFixed(1)} | 速度: VX=${this.marble.vx.toFixed(1)}, VY=${this.marble.vy.toFixed(1)}`;
    }
  },
  data() {
    return {
      themeConfigPage: "gyroscopeMaze",
      canvas: null,
      ctx: null,
      gameRunning: false,
      gameWon: false,
      requestingPermission: false,
      gyroAvailable: false,
      gyroDenied: false,
      gyroSupported: true,
      gyroData: { x: 0, y: 0 },
      marble: {
        x: 50,
        y: 50,
        radius: 15,
        vx: 0,
        vy: 0,
        friction: 0.99,
        maxSpeed: 10
      },
      goal: {
        x: 0,
        y: 0,
        radius: 25
      },
      obstacles: [
        { x: 200, y: 150, width: 80, height: 20 },
        { x: 400, y: 250, width: 20, height: 100 },
        { x: 150, y: 350, width: 120, height: 20 },
        { x: 500, y: 100, width: 20, height: 80 },
        { x: 300, y: 400, width: 100, height: 20 },
        { x: 600, y: 300, width: 20, height: 60 },
        { x: 250, y: 200, width: 60, height: 20 },
        { x: 450, y: 350, width: 20, height: 80 }
      ],
      event: null,
      animationId: null
    };
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    async init() {
      this.setupCanvas();
      this.setupGoal();
      this.gameLoop();
      this.setupKeyboardControls();
    },
    setupCanvas() {
      this.canvas = this.$refs.gameCanvas;
      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();
      window.addEventListener('resize', this.resizeCanvas);
    },
    resizeCanvas() {
      const container = this.canvas.parentElement;
      this.canvas.width = container.clientWidth;
      this.canvas.height = container.clientHeight;
      this.setupGoal(); // 重新設置終點位置
    },
    setupGoal() {
      this.goal.x = this.canvas.width - 80;
      this.goal.y = this.canvas.height - 80;
    },
    initGyroscope() {
      if (window.DeviceOrientationEvent) {
        if (typeof DeviceOrientationEvent.requestPermission === 'function') {
          this.requestingPermission = true;
          DeviceOrientationEvent.requestPermission()
            .then(permissionState => {
              this.requestingPermission = false;
              if (permissionState === 'granted') {
                window.addEventListener('deviceorientation', this.handleOrientation);
                this.gyroAvailable = true;
                return true;
              } else {
                this.gyroDenied = true;
                return false;
              }
            })
            .catch(error => {
              this.requestingPermission = false;
              console.error('陀螺儀權限請求失敗:', error);
              return false;
            });
        } else {
          window.addEventListener('deviceorientation', this.handleOrientation);
          this.gyroAvailable = true;
          return true;
        }
      } else {
        this.gyroSupported = false;
        return false;
      }
    },
    handleOrientation(event) {
      if (!this.gameRunning) return;
      
      this.gyroData.x = event.beta || 0;
      this.gyroData.y = event.gamma || 0;
      
      const sensitivity = 0.05;
      this.marble.vx += (this.gyroData.y * sensitivity);
      this.marble.vy += (this.gyroData.x * sensitivity);
      
      this.marble.vx = Math.max(-this.marble.maxSpeed, Math.min(this.marble.maxSpeed, this.marble.vx));
      this.marble.vy = Math.max(-this.marble.maxSpeed, Math.min(this.marble.maxSpeed, this.marble.vy));
    },
    checkCollision(rect, circle) {
      const distX = Math.abs(circle.x - rect.x - rect.width / 2);
      const distY = Math.abs(circle.y - rect.y - rect.height / 2);

      if (distX > (rect.width / 2 + circle.radius)) return false;
      if (distY > (rect.height / 2 + circle.radius)) return false;

      if (distX <= (rect.width / 2)) return true;
      if (distY <= (rect.height / 2)) return true;

      const dx = distX - rect.width / 2;
      const dy = distY - rect.height / 2;
      return (dx * dx + dy * dy <= (circle.radius * circle.radius));
    },
    update() {
      if (!this.gameRunning) return;

      this.marble.vx *= this.marble.friction;
      this.marble.vy *= this.marble.friction;

      this.marble.x += this.marble.vx;
      this.marble.y += this.marble.vy;

      // 邊界碰撞檢測
      if (this.marble.x - this.marble.radius < 0) {
        this.marble.x = this.marble.radius;
        this.marble.vx *= -0.5;
      }
      if (this.marble.x + this.marble.radius > this.canvas.width) {
        this.marble.x = this.canvas.width - this.marble.radius;
        this.marble.vx *= -0.5;
      }
      if (this.marble.y - this.marble.radius < 0) {
        this.marble.y = this.marble.radius;
        this.marble.vy *= -0.5;
      }
      if (this.marble.y + this.marble.radius > this.canvas.height) {
        this.marble.y = this.canvas.height - this.marble.radius;
        this.marble.vy *= -0.5;
      }

      // 障礙物碰撞檢測
      this.obstacles.forEach(obstacle => {
        if (this.checkCollision(obstacle, this.marble)) {
          const centerX = obstacle.x + obstacle.width / 2;
          const centerY = obstacle.y + obstacle.height / 2;
          
          if (Math.abs(this.marble.x - centerX) > Math.abs(this.marble.y - centerY)) {
            if (this.marble.x < centerX) {
              this.marble.x = obstacle.x - this.marble.radius;
            } else {
              this.marble.x = obstacle.x + obstacle.width + this.marble.radius;
            }
            this.marble.vx *= -0.5;
          } else {
            if (this.marble.y < centerY) {
              this.marble.y = obstacle.y - this.marble.radius;
            } else {
              this.marble.y = obstacle.y + obstacle.height + this.marble.radius;
            }
            this.marble.vy *= -0.5;
          }
        }
      });

      // 檢查是否到達終點
      const distanceToGoal = Math.sqrt(
        Math.pow(this.marble.x - this.goal.x, 2) + Math.pow(this.marble.y - this.goal.y, 2)
      );
      
      if (distanceToGoal < this.goal.radius + this.marble.radius) {
        this.gameWon = true;
        this.gameRunning = false;
      }
    },
    draw() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // 繪製背景漸層
      const gradient = this.ctx.createLinearGradient(0, 0, this.canvas.width, this.canvas.height);
      gradient.addColorStop(0, 'rgba(102, 126, 234, 0.1)');
      gradient.addColorStop(1, 'rgba(118, 75, 162, 0.1)');
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

      // 繪製障礙物
      this.ctx.fillStyle = '#ff4757';
      this.obstacles.forEach(obstacle => {
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        this.ctx.shadowBlur = 5;
        this.ctx.shadowOffsetX = 2;
        this.ctx.shadowOffsetY = 2;
        this.ctx.fillRect(obstacle.x, obstacle.y, obstacle.width, obstacle.height);
        this.ctx.shadowColor = 'transparent';
      });

      // 繪製終點
      this.ctx.fillStyle = '#2ed573';
      this.ctx.beginPath();
      this.ctx.arc(this.goal.x, this.goal.y, this.goal.radius, 0, Math.PI * 2);
      this.ctx.fill();
      
      this.ctx.shadowColor = '#2ed573';
      this.ctx.shadowBlur = 20;
      this.ctx.beginPath();
      this.ctx.arc(this.goal.x, this.goal.y, this.goal.radius, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.shadowColor = 'transparent';

      // 繪製彈珠
      this.ctx.fillStyle = '#3742fa';
      this.ctx.beginPath();
      this.ctx.arc(this.marble.x, this.marble.y, this.marble.radius, 0, Math.PI * 2);
      this.ctx.fill();
      
      this.ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      this.ctx.beginPath();
      this.ctx.arc(this.marble.x - 5, this.marble.y - 5, this.marble.radius / 3, 0, Math.PI * 2);
      this.ctx.fill();

      // 繪製彈珠軌跡
      if (Math.abs(this.marble.vx) > 0.1 || Math.abs(this.marble.vy) > 0.1) {
        this.ctx.strokeStyle = 'rgba(55, 66, 250, 0.3)';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.moveTo(this.marble.x, this.marble.y);
        this.ctx.lineTo(this.marble.x - this.marble.vx * 3, this.marble.y - this.marble.vy * 3);
        this.ctx.stroke();
      }
    },
    gameLoop() {
      this.update();
      this.draw();
      this.animationId = requestAnimationFrame(this.gameLoop);
    },
    startGame() {
      this.resetMarble();
      this.initGyroscope();
      this.gameRunning = true;
      this.gameWon = false;
    },
    resetGame() {
      this.gameRunning = false;
      this.gameWon = false;
      this.resetMarble();
    },
    resetMarble() {
      this.marble.x = 50;
      this.marble.y = 50;
      this.marble.vx = 0;
      this.marble.vy = 0;
    },
    testMovement() {
      if (!this.gameRunning) {
        this.gameRunning = true;
      }
      this.marble.vx = 3;
      this.marble.vy = 2;
    },
    setupKeyboardControls() {
      document.addEventListener('keydown', this.handleKeydown);
    },
    handleKeydown(e) {
      if (!this.gameRunning) return;
      
      const speed = 3;
      switch(e.key) {
        case 'ArrowUp':
          this.marble.vy -= speed;
          break;
        case 'ArrowDown':
          this.marble.vy += speed;
          break;
        case 'ArrowLeft':
          this.marble.vx -= speed;
          break;
        case 'ArrowRight':
          this.marble.vx += speed;
          break;
        case ' ':
          this.resetMarble();
          break;
      }
    },
    cleanup() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      window.removeEventListener('resize', this.resizeCanvas);
      window.removeEventListener('deviceorientation', this.handleOrientation);
      document.removeEventListener('keydown', this.handleKeydown);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../assets/css/liff/main.css";
@import "../../../assets/css/liff/liff_init_theme_config.css";

.s-layout {
  color: var(--liff-primary_text_color);
  background-color: var(--liff-layout_bg_color);
  --s-gray: #979797 !important;
  --s-gray-light: #f0f0f0 !important;
}

.s-container {
  min-height: 100vh;
}

.page {
  padding: 16px 12px;
  background: var(--liff-content_bg_color);
  &__title {
    font-size: 20px;
    line-height: 28px;
    font-weight: 500;
    margin-bottom: 16px;
    text-align: center;
    color: var(--liff-primary_text_color) !important;
  }
}

.game-container {
  position: relative;
  width: 100%;
  height: 70vh;
  max-height: 600px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  margin-bottom: 20px;
}

#gameCanvas {
  width: 100%;
  height: 100%;
  display: block;
}

.controls {
  text-align: center;
  color: var(--liff-primary_text_color);
  margin-top: 20px;
}

.info {
  margin-top: 15px;
  color: var(--liff-secondary_text_color);
  font-size: 14px;
}

.status {
  position: absolute;
  top: 20px;
  left: 20px;
  color: white;
  font-size: 16px;
  z-index: 10;
}

.win-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  font-size: 24px;
  z-index: 20;
}

.instructions {
  position: absolute;
  bottom: 20px;
  left: 20px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  max-width: 200px;
}
</style> 